import { isFutureDate, parseDateAsLocal } from '@heidi/utils/dates';

import type { PatientProfileWithSessionCount } from '../../contracts';

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

export function capitalize(s: string): string;
export function capitalize(s: null | undefined): null;
export function capitalize(s: string | null | undefined): string | null;
export function capitalize(s: string | null | undefined): string | null {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : null;
}

export const getDisplayName = (p: PatientProfileWithSessionCount): string => {
  // Prefer the identifier the clinician typed in — it's always there when a
  // patient exists, whereas structured first/last only get populated when
  // auto-extraction has run and succeeded.
  const identifier = p.demographic_string?.trim();
  if (identifier) return identifier;
  const full = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
  return full || '—';
};

/** Best-effort human name (first + last), falling back to the identifier
 *  when the structured names haven't been populated. Use this sparingly —
 *  for the primary label of a patient surface, prefer `getDisplayName`. */
export const getFullName = (p: PatientProfileWithSessionCount): string => {
  const full = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
  return full || p.demographic_string || '—';
};

export const calculateAge = (isoDate: string | null | undefined): number | null => {
  if (!isoDate) return null;
  const dob = parseDateAsLocal(String(isoDate));
  if (!dob || Number.isNaN(dob.getTime())) return null;
  const age = Math.floor((Date.now() - dob.getTime()) / MS_PER_YEAR);
  return age >= 0 ? age : null;
};

/** True when a birth-date value (ISO `yyyy-MM-dd`, ISO datetime, or compact
 *  form) is in the future. Empty / unparseable input returns false so only a
 *  genuinely future date is flagged. Shared by the create form and the inline
 *  profile editor so both reject future dates of birth identically. */
export const isFutureDob = (isoDate: string | null | undefined): boolean => {
  const dob = parseDateAsLocal(isoDate ?? undefined);
  return dob ? isFutureDate(dob) : false;
};
